import {
  forwardRef,
  type HTMLAttributes,
  useContext,
  useEffect,
  useState,
} from 'react';
import cn from 'classnames';

import { ClaimCashCardAnimation } from '@/components/Animations/ClaimItemAnimation/ClaimCashCardAnimation/ClaimCashCardAnimation';
import { StringXSmall } from '@/components/Typography/Typography';
import { MotionPicture } from '@/components/ui/Picture/Picture';
import { selectMiniGameReward } from '@/redux/selectors/diceGameSelectors';
import { useAppSelector } from '@/redux/store';

import { DiceGameContext } from '../../context';
import { Tile } from '../Tile/Tile';
import { TilesBg, type TilesBgVariantType } from '../TilesBg/TilesBg';

import css from './styles.module.scss';

import tgemPng from '/img/tgem.png';
import tgemWebp from '/img/tgem.webp';

interface CoinTileProps extends HTMLAttributes<HTMLDivElement> {
  isSuperGame?: boolean;
  isActive?: boolean;
  variant?: 'mini-game' | 'start' | 'default';
  value?: number;
}

const CoinMiniGameTile = forwardRef<HTMLDivElement, CoinTileProps>(
  ({ variant = 'default', value, isSuperGame, ...props }, ref) => {
    const tgemFinalPosition = useAppSelector(
      (state) => state.diceGame.position.rewards.tgem,
    );
    const reward = useAppSelector(selectMiniGameReward);
    const [animationFinished, setAnimationFinished] = useState(false);
    const { setIsRewardScaled } = useContext(DiceGameContext);

    const bgVariants = {
      'mini-game': 'purple-fill',
      'start': 'start',
      'default': 'purple',
    } as const;

    const activeBgVariants = {
      'mini-game': 'purple-active',
      'start': 'start',
      'default': 'purple-active',
    } as const;

    const isAnimating =
      props.isActive && isSuperGame && !!reward && !animationFinished;

    useEffect(() => {
      if (isAnimating && (value !== 0 || variant !== 'mini-game')) {
        setIsRewardScaled(true);
      }
    }, [isAnimating, value]);

    const getTilesBgVariant = (): TilesBgVariantType => {
      if (value === 0 && variant === 'mini-game') {
        return 'white';
      }

      if (props.isActive) {
        return activeBgVariants[variant];
      }

      return bgVariants[variant];
    };

    return (
      <Tile
        ref={ref}
        {...props}
        variant={props.isActive ? 'dark' : 'grey'}
        className={cn(
          props.className,
          'flex items-center justify-center !bg-transparent',
        )}
      >
        {variant === 'start' && (
          <StringXSmall
            weight="bold"
            className="absolute z-[3] left-1/2 -translate-x-1/2 top-[7.14%]"
          >
            Start
          </StringXSmall>
        )}
        {value === 0 && variant === 'mini-game' ? (
          <span
            className={cn(
              'font-black text-[2rem] text-[#F0F0F0] leading-none relative z-[2]',
              css.zeroShadow,
            )}
          >
            0
          </span>
        ) : (
          <div className="relative z-[2] size-[80%] flex">
            <MotionPicture
              className="w-full h-full"
              img={tgemPng}
              webp={tgemWebp}
              alt="tgem"
              animate={{ scale: isAnimating ? 1.2 : 1 }}
              transition={{
                duration: 0.3,
                delay: 0,
              }}
            />
            {isAnimating && (
              <ClaimCashCardAnimation
                icon={{ img: tgemPng, webp: tgemWebp }}
                reward="tgem"
                finalPositon={tgemFinalPosition}
                type="arc"
                onAnimationEnd={() => {
                  setIsRewardScaled(false);
                  setAnimationFinished(true);
                }}
                scale={1.4}
                startScale={1.2}
              />
            )}
          </div>
        )}
        {!!value && (
          <div className={css.tileValue}>
            <StringXSmall weight="bold">{value}</StringXSmall>
          </div>
        )}
        <TilesBg
          className={cn(!props.isActive && '!top-0 !left-0 !w-full !h-full')}
          variant={getTilesBgVariant()}
          withStarsBg={
            (variant === 'mini-game' && value !== 0) || variant === 'start'
          }
        />
      </Tile>
    );
  },
);

export { CoinMiniGameTile };
